import { JxsComponent } from '@jxs/component';
import { debounce } from '@jxs/utilities';

const STICKY_SECTION_CLASS = 'jxs-sticky-tabbar-section';
const OBSERVER_THRESHOLDS = [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1];
const ACTIVE_RATIO_THRESHOLD = 0.2;
const RATIO_TOLERANCE = 0.001;
const STICKY_TOP_SELECTOR = [
  'header',
  'sticky-header',
  '[role="banner"]',
  '.shopify-section-header',
  '.section-header',
  '.site-header',
  '.header',
  '.header-wrapper',
  '.announcement-bar',
  '.announcement-bar-section',
].join(',');

/**
 * @typedef {{ id: string, element: HTMLElement, tab: HTMLElement | null, isLinked: boolean }} JxsStickyTabbarSection
 * @typedef {{ ratio: number, previousRatio: number, top: number, bottom: number, element: Element, isIntersecting: boolean, isLeaving: boolean }} JxsStickyTabbarVisibility
 * @typedef {{ id: string, ratio: number, top: number, index: number, isLeaving: boolean, isLinked: boolean }} JxsStickyTabbarVisibleSection
 * @typedef {'auto' | 'custom'} JxsStickyTopType
 * @typedef {{ top: number, bottom: number }} JxsStickyTopInterval
 */

/**
 * 仅供固定标签栏使用的顶部占位观察器。
 *
 * 通过扫描当前页面顶部实际可见的 fixed/sticky 元素，自动更新目标元素的 `--sticky-top`。
 * 计算基于 `getBoundingClientRect()`，可感知导航栏高度变化、显示隐藏和 transform 半隐藏状态。
 */
class StickyTopObserver {
  /** @type {HTMLElement} */
  #target;

  /** @type {HTMLElement | null} */
  #excludeElement;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** @type {MutationObserver | null} */
  #mutationObserver = null;

  /** @type {Set<HTMLElement>} */
  #elements = new Set();

  /** @type {number} */
  #value = -1;

  /** @type {number} */
  #updateFrame = 0;

  #refreshElementsDebounced = debounce(() => {
    this.#refreshElements();
  }, 150);

  #onScroll = () => {
    this.requestUpdate();
  };

  #onResize = () => {
    this.requestUpdate();
  };

  #onMutation = () => {
    this.requestUpdate();
    this.#refreshElementsDebounced();
  };

  /**
   * @param {HTMLElement} target
   * @param {HTMLElement | null} excludeElement
   */
  constructor(target, excludeElement = null) {
    this.#target = target;
    this.#excludeElement = excludeElement;
  }

  /**
   * @returns {number} 当前顶部占位高度。
   */
  get value() {
    return Math.max(this.#value, 0);
  }

  connect() {
    this.#refreshElements();
    this.update();

    window.addEventListener('scroll', this.#onScroll, { passive: true });
    window.addEventListener('resize', this.#onResize);

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', this.#onResize);
      window.visualViewport.addEventListener('scroll', this.#onScroll);
    }

    if (typeof MutationObserver !== 'undefined') {
      this.#mutationObserver = new MutationObserver(this.#onMutation);
      this.#mutationObserver.observe(document.documentElement, {
        attributes: true,
        childList: true,
        subtree: true,
        attributeFilter: ['class', 'style', 'hidden', 'aria-hidden'],
      });
    }
  }

  disconnect() {
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;

    this.#mutationObserver?.disconnect();
    this.#mutationObserver = null;

    this.#refreshElementsDebounced.cancel?.();

    if (this.#updateFrame) {
      window.cancelAnimationFrame(this.#updateFrame);
      this.#updateFrame = 0;
    }

    window.removeEventListener('scroll', this.#onScroll);
    window.removeEventListener('resize', this.#onResize);

    if (window.visualViewport) {
      window.visualViewport.removeEventListener('resize', this.#onResize);
      window.visualViewport.removeEventListener('scroll', this.#onScroll);
    }

    this.#elements.clear();
    this.#target.style.removeProperty('--sticky-top');
  }

  requestUpdate() {
    if (this.#updateFrame) return;

    this.#updateFrame = window.requestAnimationFrame(() => {
      this.#updateFrame = 0;
      this.update();
    });
  }

  /**
   * 立即重新计算顶部占位。
   *
   * @returns {number}
   */
  update() {
    const value = this.#calculate();

    if (Math.abs(value - this.#value) >= 0.5) {
      this.#value = value;
      this.#target.style.setProperty('--sticky-top', `${Math.round(value * 100) / 100}px`);
    }

    return this.value;
  }

  #refreshElements() {
    this.#elements = this.#getCandidates();

    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;

    if (typeof ResizeObserver !== 'undefined') {
      this.#resizeObserver = new ResizeObserver(() => {
        this.requestUpdate();
      });

      this.#elements.forEach((element) => {
        this.#resizeObserver?.observe(element);
      });
    }

    this.requestUpdate();
  }

  /**
   * @returns {Set<HTMLElement>}
   */
  #getCandidates() {
    /** @type {Set<HTMLElement>} */
    const elements = new Set();

    document.querySelectorAll(STICKY_TOP_SELECTOR).forEach((element) => {
      if (element instanceof HTMLElement) {
        elements.add(element);
      }
    });

    document.body?.querySelectorAll('*').forEach((element) => {
      if (!(element instanceof HTMLElement)) return;

      const position = window.getComputedStyle(element).position;

      if (position === 'fixed' || position === 'sticky') {
        elements.add(element);
      }
    });

    return elements;
  }

  /**
   * @returns {number}
   */
  #calculate() {
    /** @type {JxsStickyTopInterval[]} */
    const intervals = [];
    const viewportWidth = document.documentElement.clientWidth || window.innerWidth || 0;
    const minTopElementWidth = Math.min(viewportWidth * 0.5, 320);

    this.#elements.forEach((element) => {
      if (!this.#isValidCandidate(element)) return;

      const rect = element.getBoundingClientRect();
      const horizontalOverlap = Math.min(rect.right, viewportWidth) - Math.max(rect.left, 0);

      if (horizontalOverlap < minTopElementWidth) return;

      intervals.push({
        top: Math.max(rect.top, 0),
        bottom: Math.max(rect.bottom, 0),
      });
    });

    if (intervals.length === 0) return 0;

    intervals.sort((a, b) => a.top - b.top || a.bottom - b.bottom);

    let stickyTop = 0;

    intervals.some((interval) => {
      if (interval.top > stickyTop + 1) {
        return stickyTop === 0;
      }

      stickyTop = Math.max(stickyTop, interval.bottom);
      return false;
    });

    return Math.max(stickyTop, 0);
  }

  /**
   * @param {HTMLElement} element
   * @returns {boolean}
   */
  #isValidCandidate(element) {
    if (!element.isConnected) return false;
    if (this.#excludeElement && (element === this.#excludeElement || this.#excludeElement.contains(element))) {
      return false;
    }

    const style = window.getComputedStyle(element);

    if (style.position !== 'fixed' && style.position !== 'sticky') return false;
    if (style.display === 'none' || style.visibility === 'hidden') return false;
    if (Number.parseFloat(style.opacity) <= 0) return false;

    const rect = element.getBoundingClientRect();
    const viewportWidth = document.documentElement.clientWidth || window.innerWidth || 0;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;

    if (rect.width <= 0 || rect.height <= 0) return false;
    if (rect.bottom <= 0 || rect.top >= viewportHeight) return false;
    if (rect.right <= 0 || rect.left >= viewportWidth) return false;
    if (rect.top <= 1 && rect.bottom >= viewportHeight - 1) return false;

    return true;
  }
}

/**
 * 固定标签栏组件。
 *
 * 组件用于把一组标签与页面中的 section 进行关联：
 * - 点击标签时，页面会平滑滚动到标签配置的目标 section。
 * - 页面滚动时，组件会根据目标 section 在视口中的可见状态自动切换激活标签。
 * - 当设置 `use-sticky` 属性时，组件所在的 Shopify section 会固定在页面顶部。
 * - `data-sticky-top-type="auto"` 时，组件会自动更新 `--sticky-top`，避让页面顶部实际可见的 fixed/sticky 元素。
 * - `data-sticky-top-type="custom"` 时，组件使用外部传入的 `--sticky-top`，不会自动计算。
 * - `.jxs-sticky-tabbar__active-el` 是标签激活态的视觉游标，由 Liquid 输出，JS 只负责同步它的位置与尺寸。
 *
 * 激活标签规则：
 * - 页面 Template 中的普通 section 和存在对应标签的 section 都会参与可见比例比较。
 * - section 进入视口且可见比例大于 0 时，会参与激活态计算。
 * - 当只有一个有关联标签的 section 可见时，进入可见比例达到 20% 才会激活，离开可见比例降到 20% 时清除激活态。
 * - 当多个 section 同时可见时，优先比较当前可见比例最高的 section。
 * - 当多个 section 的可见比例相同时，优先比较 DOM 顺序更靠前的 section。
 * - 只有最终胜出的 section 存在对应标签时才会激活标签，否则会清除所有标签的激活状态并隐藏激活游标。
 *
 * 标签优先通过 `data-section-id` 指向目标 section；当 `data-section-id` 为空时，
 * 会使用 `data-section-number` 按主题编辑器 Template 组中的 Shopify section 序号关联。
 * 填写 `data-section-id-end` 后，开始与结束 section 之间的模块（含两端）都关联同一个标签。
 */
export class JxsStickyTabbar extends JxsComponent {
  /** @type {StickyTopObserver | null} */
  #stickyTopObserver = null;

  /** @type {ResizeObserver | null} */
  #activeElementResizeObserver = null;

  /** @type {HTMLElement | null} */
  #tabList = null;

  /** @type {HTMLElement | null} */
  #activeElement = null;

  /** @type {IntersectionObserver | null} */
  #intersectionObserver = null;

  /** @type {JxsStickyTabbarSection[]} */
  #sections = [];

  /** @type {Map<string, JxsStickyTabbarVisibility>} */
  #sectionVisibility = new Map();

  /** @type {string | null} */
  #activeSectionId = null;

  #onWindowResize = debounce(() => {
    this.#updateActiveElement();
  }, 100);

  /**
   * @param {Event} event
   */
  #onTabClick = (event) => {
    const tab = event.currentTarget;

    if (!(tab instanceof HTMLElement)) return;

    const targetSection = this.#getTargetSectionByTab(tab);

    if (!targetSection) return;

    const targetPosition =
      targetSection.getBoundingClientRect().top + window.scrollY - this.#getStickyTop() - this.offsetHeight;

    window.scrollTo({
      top: Math.max(targetPosition, 0),
      behavior: 'smooth',
    });
  };

  /**
   * @param {IntersectionObserverEntry[]} entries
   */
  #onSectionIntersection = (entries) => {
    entries.forEach((entry) => {
      const section = this.#sections.find((item) => item.element === entry.target);

      if (!section) return;

      const rect = entry.target.getBoundingClientRect();
      const previousVisibility = this.#sectionVisibility.get(section.id);
      const ratio = entry.intersectionRatio;

      this.#sectionVisibility.set(section.id, {
        ratio,
        previousRatio: previousVisibility?.ratio ?? 0,
        top: rect.top,
        bottom: rect.bottom,
        element: entry.target,
        isIntersecting: entry.isIntersecting,
        isLeaving: previousVisibility ? ratio < previousVisibility.ratio - RATIO_TOLERANCE : false,
      });
    });

    /** @type {JxsStickyTabbarVisibleSection[]} */
    const visibleSections = [];

    this.#sections.forEach((section, index) => {
      const data = this.#sectionVisibility.get(section.id);

      if (!data || !data.isIntersecting || data.ratio <= 0) return;

      visibleSections.push({
        id: section.id,
        ratio: data.ratio,
        top: data.top,
        index,
        isLeaving: data.isLeaving,
        isLinked: section.isLinked,
      });
    });

    if (visibleSections.length === 0) {
      if (this.#activeSectionId !== null) {
        this.#activeSectionId = null;
        this.clearActiveTabs();
      }

      return;
    }

    const nextActiveSectionId = this.#getNextActiveSectionId(visibleSections);

    if (nextActiveSectionId === this.#activeSectionId) return;

    this.#activeSectionId = nextActiveSectionId;

    if (nextActiveSectionId) {
      this.setActiveTab(nextActiveSectionId);
    } else {
      this.clearActiveTabs();
    }
  };

  /**
   * @param {JxsStickyTabbarVisibleSection[]} visibleSections
   * @returns {string | null}
   */
  #getNextActiveSectionId(visibleSections) {
    const [section] = visibleSections;

    if (visibleSections.length === 1 && section) {
      if (!section.isLinked) return null;

      const isCurrentActive = section.id === this.#activeSectionId;

      if (isCurrentActive) {
        return section.isLeaving && section.ratio <= ACTIVE_RATIO_THRESHOLD + RATIO_TOLERANCE ? null : section.id;
      }

      return !section.isLeaving && section.ratio >= ACTIVE_RATIO_THRESHOLD - RATIO_TOLERANCE ? section.id : null;
    }

    const maxRatio = Math.max(...visibleSections.map((section) => section.ratio));
    const maxRatioSections = visibleSections
      .filter((section) => Math.abs(section.ratio - maxRatio) < RATIO_TOLERANCE)
      .sort((a, b) => a.index - b.index);
    const [sectionWithMaxRatio] = maxRatioSections;

    return sectionWithMaxRatio?.isLinked ? sectionWithMaxRatio.id : null;
  }

  connectedCallback() {
    super.connectedCallback();
    this.#cacheElements();

    if (this.useSticky) {
      this.#initSticky();
    }

    this.#initActiveElement();
    this.#bindTabClickEvents();
    this.#initScrollObserver();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#destroySticky();
    this.#destroyActiveElement();
    this.#unbindTabClickEvents();
    this.#intersectionObserver?.disconnect();
    this.#intersectionObserver = null;
    this.#sections = [];
    this.#sectionVisibility.clear();
    this.#tabList = null;
    this.#activeElement = null;
  }

  /**
   * @returns {HTMLElement|null} 当前组件所在的 Shopify section 元素。
   */
  get section() {
    return this.closest('.shopify-section');
  }

  /**
   * @returns {NodeListOf<HTMLElement>} 所有标签元素。
   */
  get tabItems() {
    return this.querySelectorAll('.jxs-sticky-tabbar__item');
  }

  /**
   * @returns {boolean} 是否启用 sticky 定位。
   */
  get useSticky() {
    return this.hasAttribute('use-sticky');
  }

  /**
   * @returns {JxsStickyTopType} 吸顶偏移方式。
   */
  get stickyTopType() {
    return this.dataset.stickyTopType === 'custom' ? 'custom' : 'auto';
  }

  /**
   * @returns {boolean} 是否自动计算吸顶偏移。
   */
  get useAutoStickyTop() {
    return this.stickyTopType === 'auto';
  }

  #cacheElements() {
    const tabList = this.querySelector('.jxs-sticky-tabbar__item-list');
    const activeElement = this.querySelector('.jxs-sticky-tabbar__active-el');

    this.#tabList = tabList instanceof HTMLElement ? tabList : null;
    this.#activeElement = activeElement instanceof HTMLElement ? activeElement : null;
  }

  #initSticky() {
    const section = this.section;

    if (!section) return;

    section.classList.add(STICKY_SECTION_CLASS);

    if (this.useAutoStickyTop) {
      this.#stickyTopObserver = new StickyTopObserver(section, section);
      this.#stickyTopObserver.connect();
    }

    window.addEventListener('resize', this.#onWindowResize);
  }

  #destroySticky() {
    this.#stickyTopObserver?.disconnect();
    this.#stickyTopObserver = null;
    this.#onWindowResize.cancel?.();
    window.removeEventListener('resize', this.#onWindowResize);
    this.section?.classList.remove(STICKY_SECTION_CLASS);
  }

  /**
   * @returns {number}
   */
  #getStickyTop() {
    if (this.useAutoStickyTop) {
      return this.#stickyTopObserver?.update() ?? 0;
    }

    return this.#getStickyTopFromCSS();
  }

  /**
   * @returns {number}
   */
  #getStickyTopFromCSS() {
    const target = this.section || this;
    const value = window.getComputedStyle(target).getPropertyValue('--sticky-top');
    const stickyTop = Number.parseFloat(value);

    return Number.isFinite(stickyTop) ? stickyTop : 0;
  }

  #bindTabClickEvents() {
    this.tabItems.forEach((tab) => {
      tab.addEventListener('click', this.#onTabClick);
    });
  }

  #unbindTabClickEvents() {
    this.tabItems.forEach((tab) => {
      tab.removeEventListener('click', this.#onTabClick);
    });
  }

  #initActiveElement() {
    if (!this.#tabList || !this.#activeElement) return;

    this.#activeElement.setAttribute('aria-hidden', 'true');

    if (typeof ResizeObserver !== 'undefined') {
      const resizeObserver = new ResizeObserver(() => {
        this.#updateActiveElement();
      });

      resizeObserver.observe(this.#tabList);
      this.tabItems.forEach((tab) => {
        resizeObserver.observe(tab);
      });

      this.#activeElementResizeObserver = resizeObserver;
    }

    this.#updateActiveElement();
  }

  #destroyActiveElement() {
    this.#activeElementResizeObserver?.disconnect();
    this.#activeElementResizeObserver = null;
  }

  #updateActiveElement() {
    const activeTab = this.#getActiveTab();

    if (!this.#tabList || !this.#activeElement || !activeTab) {
      this.#activeElement?.removeAttribute('data-active');
      return;
    }

    this.#activeElement.style.setProperty('--active-el-x', `${activeTab.offsetLeft}px`);
    this.#activeElement.style.setProperty('--active-el-y', `${activeTab.offsetTop}px`);
    this.#activeElement.style.setProperty('--active-el-width', `${activeTab.offsetWidth}px`);
    this.#activeElement.style.setProperty('--active-el-height', `${activeTab.offsetHeight}px`);
    this.#activeElement.setAttribute('data-active', 'true');
  }

  /**
   * @returns {HTMLElement|null}
   */
  #getActiveTab() {
    const element = this.querySelector('.jxs-sticky-tabbar__item[active]');

    return element instanceof HTMLElement ? element : null;
  }

  #initScrollObserver() {
    this.#intersectionObserver?.disconnect();
    this.#sectionVisibility.clear();
    this.#sections = this.#getSections();

    if (this.#sections.length === 0) return;

    const observer = new IntersectionObserver(this.#onSectionIntersection, {
      threshold: OBSERVER_THRESHOLDS,
    });

    this.#sections.forEach((section) => {
      observer.observe(section.element);
    });

    this.#intersectionObserver = observer;
  }

  #getSections() {
    const templateSections = this.#getTemplateSections().filter((section) => section !== this.section);
    /** @type {Map<string, HTMLElement>} */
    const linkedTabs = new Map();

    this.tabItems.forEach((tab) => {
      const start = this.#getTargetSectionByTab(tab);

      if (!start) return;

      this.#getSectionRange(templateSections, start, this.#getTargetSection(tab.dataset.sectionIdEnd)).forEach(
        (section) => {
          if (linkedTabs.has(section.id)) return;

          linkedTabs.set(section.id, tab);
        },
      );
    });

    /** @type {JxsStickyTabbarSection[]} */
    const sections = templateSections.map((section) => {
        const tab = linkedTabs.get(section.id) || null;

        return {
          id: section.id,
          element: section,
          tab,
          isLinked: Boolean(tab),
        };
      });

    linkedTabs.forEach((tab, sectionId) => {
      const element = this.#getTargetSection(sectionId);

      if (!element || sections.some((section) => section.id === sectionId)) return;

      sections.push({
        id: sectionId,
        element,
        tab,
        isLinked: true,
      });
    });

    return sections.sort((a, b) => {
      if (a.element === b.element) return 0;

      const position = a.element.compareDocumentPosition(b.element);

      return position & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1;
    });
  }

  /**
   * 设置指定 section 对应的标签为激活状态。
   *
   * @param {string} sectionId
   */
  setActiveTab(sectionId) {
    const activeTab = this.#sections.find((section) => section.id === sectionId)?.tab || null;

    this.tabItems.forEach((tab) => {
      tab.toggleAttribute('active', tab === activeTab);
    });

    this.#updateActiveElement();
  }

  /**
   * 清除所有标签的激活状态。
   */
  clearActiveTabs() {
    this.tabItems.forEach((tab) => {
      tab.removeAttribute('active');
    });

    this.#activeElement?.removeAttribute('data-active');
  }

  /**
   * @param {HTMLElement} tab
   * @returns {HTMLElement | null}
   */
  #getTargetSectionByTab(tab) {
    const sectionId = tab.dataset.sectionId?.trim();

    if (sectionId) {
      return this.#getTargetSection(sectionId);
    }

    return this.#getTargetSectionByNumber(tab.dataset.sectionNumber);
  }

  /**
   * @param {string | undefined} sectionId
   * @returns {HTMLElement | null}
   */
  #getTargetSection(sectionId) {
    const normalizedSectionId = sectionId?.trim().replace(/^#/, '');

    if (!normalizedSectionId) return null;

    const wrapper = document.getElementById(`shopify-section-${normalizedSectionId}`);

    if (wrapper) return wrapper;

    const element = document.getElementById(normalizedSectionId);

    if (!element) return null;

    return element.closest('.shopify-section') || element;
  }

  /**
   * @param {HTMLElement[]} templateSections
   * @param {HTMLElement} start
   * @param {HTMLElement | null} end
   * @returns {HTMLElement[]}
   */
  #getSectionRange(templateSections, start, end) {
    const startIndex = templateSections.indexOf(start);
    const endIndex = end ? templateSections.indexOf(end) : -1;

    if (startIndex < 0) return [start];
    if (!end || endIndex < 0) return [templateSections[startIndex]];

    const from = Math.min(startIndex, endIndex);
    const to = Math.max(startIndex, endIndex);

    return templateSections.slice(from, to + 1);
  }

  /**
   * @param {string | undefined} sectionNumber
   * @returns {HTMLElement | null}
   */
  #getTargetSectionByNumber(sectionNumber) {
    const index = Number.parseInt(sectionNumber || '', 10) - 1;

    if (index < 0) return null;

    const section = this.#getTemplateSections()[index];

    return section || null;
  }

  /**
   * @returns {HTMLElement[]}
   */
  #getTemplateSections() {
    return Array.from(document.querySelectorAll('#MainContent .shopify-section'));
  }
}

if (!customElements.get('jxs-sticky-tabbar')) {
  customElements.define('jxs-sticky-tabbar', JxsStickyTabbar);
}